import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import RNBluetoothClassic, {
  BluetoothDevice,
} from 'react-native-bluetooth-classic';
import {
  check,
  checkMultiple,
  PERMISSIONS,
  request,
  requestMultiple,
} from 'react-native-permissions';
import { ListOption } from '@/components/list-item';
import { Platform } from 'react-native';

export type BluetoothDeviceListOption = ListOption<{
  device: BluetoothDevice;
}>;

export interface BluetoothContextType {
  canRequestPermission: boolean;
  clearSelectedDevice: () => void;
  getPairedDevices: () => Promise<BluetoothDeviceListOption[]>;
  hasPermission: boolean;
  requestPermission: () => Promise<boolean>;
  selectDevice: (device: BluetoothDevice) => Promise<boolean>;
  selectedDevice: BluetoothDevice | null;
}

export const BluetoothContext = createContext<BluetoothContextType | null>(
  null,
);

export const useBluetoothContext = () => {
  const context = useContext(BluetoothContext);
  if (!context) {
    throw new Error('useBluetoothContext requires a BluetoothContextProvider');
  }

  return context;
};

interface Props {
  children: React.ReactNode;
}

const platformVersion = Platform.Version || 0;
const apiLevel = parseInt(platformVersion.toString(), 10);
const LEVEL_REQUIRING_BLUETOOTH_PERMISSIONS = 31;
// on iOS, request only bluetooth.
const IOS_BLUE_TOOTH_PERMISSION = PERMISSIONS.IOS.BLUETOOTH;
// Android api level 31 requires bluetooth scan and connect permission.
// Earlier versions only required fine location
const requiresAndroidBluetoothPermissions =
  apiLevel >= LEVEL_REQUIRING_BLUETOOTH_PERMISSIONS;
const ANDROID_BLUETOOTH_PERMISSIONS = requiresAndroidBluetoothPermissions
  ? [
      PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION,
      PERMISSIONS.ANDROID.BLUETOOTH_SCAN,
      PERMISSIONS.ANDROID.BLUETOOTH_CONNECT,
    ]
  : [PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION];

// HACK: for some reason on iOS the first call always fails
// but the second succeeds
const setUpBluetooth = async () => {
  try {
    let isAvailable = await RNBluetoothClassic.isBluetoothEnabled();
    if (!isAvailable) {
      // delay 1s
      await new Promise((resolve) => setTimeout(resolve, 1000));
      isAvailable = await RNBluetoothClassic.isBluetoothEnabled();
    }
    return isAvailable;
  } catch {
    return false;
  }
};

export const BluetoothContextProvider: React.FC<Props> = ({ children }) => {
  const [selectedDevice, setSelectedDevice] = useState<BluetoothDevice | null>(
    null,
  );
  const [hasPermission, setHasPermission] = useState(false);
  const [canRequestPermission, setCanRequestPermission] = useState(true);

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        let nextHasPermission = false;
        if (Platform.OS === 'ios') {
          const permissionResult = await check(IOS_BLUE_TOOTH_PERMISSION);
          if (permissionResult === 'blocked') {
            setCanRequestPermission(false);
          }
          nextHasPermission = permissionResult === 'granted';
        } else if (Platform.OS === 'android') {
          const permissionResult = await checkMultiple(
            ANDROID_BLUETOOTH_PERMISSIONS,
          );
          nextHasPermission = ANDROID_BLUETOOTH_PERMISSIONS.every(
            (permission) => permissionResult[permission] === 'granted',
          );
          const cannotAskAgain = ANDROID_BLUETOOTH_PERMISSIONS.some(
            (permission) => permissionResult[permission] === 'blocked',
          );
          setCanRequestPermission(!cannotAskAgain);
        }
        if (nextHasPermission) {
          const isAvailable = await setUpBluetooth();
          if (!isAvailable) {
            nextHasPermission = false;
          }
        }
        setHasPermission(nextHasPermission);
      } catch (e) {
        // ignore
      }
    };

    checkPermissions();
  }, []);

  const value = useMemo<BluetoothContextType>(() => {
    return {
      canRequestPermission,
      clearSelectedDevice: () => {
        setSelectedDevice(null);
      },
      getPairedDevices: async () => {
        const devices = await RNBluetoothClassic.getBondedDevices();
        return devices
          .map((device) => ({
            metadata: {
              device,
            },
            title: device.name,
            value: device.address,
          }))
          .sort((a, b) => a.title.localeCompare(b.title));
      },
      hasPermission,
      requestPermission: async () => {
        if (!canRequestPermission) {
          return false;
        }
        let nextHasPermission = false;
        if (Platform.OS === 'ios') {
          const result = await request(IOS_BLUE_TOOTH_PERMISSION);
          nextHasPermission = result === 'granted';
        } else if (Platform.OS === 'android') {
          const permissionResult = await requestMultiple(
            ANDROID_BLUETOOTH_PERMISSIONS,
          );
          nextHasPermission = ANDROID_BLUETOOTH_PERMISSIONS.every(
            (permission) => permissionResult[permission] === 'granted',
          );
        }
        await setUpBluetooth();
        setCanRequestPermission(false);
        setHasPermission(nextHasPermission);
        return nextHasPermission;
      },
      selectDevice: async (device: BluetoothDevice) => {
        if (selectedDevice) {
          await selectedDevice.disconnect();
          setSelectedDevice(null);
        }
        const doesConnect = await device.connect();
        if (doesConnect) {
          setSelectedDevice(device);
          return true;
        }
        return false;
      },
      selectedDevice,
    };
  }, [canRequestPermission, hasPermission, selectedDevice]);

  return (
    <BluetoothContext.Provider value={value}>
      {children}
    </BluetoothContext.Provider>
  );
};
